import { Component, inject } from '@angular/core';
import { Router } from '@angular/router';
import { HttpClient } from '@angular/common/http';
import { MatSnackBar } from '@angular/material/snack-bar';
import { MatCardModule } from '@angular/material/card';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatTabsModule } from '@angular/material/tabs';
import { CommonModule } from '@angular/common';
import { DynamicFormsV2Component } from '../shared/dynamic-forms-v2/dynamic-forms-v2.component';
import { formMetaData, IndividualField, CodeField, FormFields } from '../shared/dynamic-forms.interface2';
import { authBackendEndPointToken } from '../app.config';
import { ErrorService } from '../shared/error.service';
import { SuccessMessageComponent } from '../shared/success-message/success-message.component';
import { forkJoin } from 'rxjs';
import { FormFieldsSelectDataService } from '../shared/form-fields-select-data.service';
import { MatButtonToggleModule } from '@angular/material/button-toggle';

@Component({
  selector: 'app-superadmin-create',
  standalone: true,
  imports: [
    CommonModule,
    MatCardModule,
    MatButtonModule,
    MatIconModule,
    MatTabsModule,
    DynamicFormsV2Component,
    MatButtonToggleModule
  ],
  templateUrl: './superadmin-create.component.html',
  styleUrl: './superadmin-create.component.scss'
})
export class SuperadminCreateComponent {
  router = inject(Router);
  http = inject(HttpClient);
  snackBar = inject(MatSnackBar);
  errorService = inject(ErrorService);
  backendApi = inject(authBackendEndPointToken);
  formFieldsDataService = inject(FormFieldsSelectDataService);

 selectedTabIndex = 0;
  formValues: any = {};
  accountType: 'SuperAdmin' | 'EventManager' = 'SuperAdmin';

  formMeta: formMetaData = {
    formName: 'SuperAdmin Registration',
    formDescription: 'Create a new SuperAdmin user',
    submitText: 'Create SuperAdmin'
  };

  formFields?: FormFields[];

  ngOnInit() {
    forkJoin({
      contactPointUse: this.formFieldsDataService.getFormFieldSelectData('patient', 'contactPointUse'),
      contactPointSystem: this.formFieldsDataService.getFormFieldSelectData('patient', 'contactPointSystem'),
    }).subscribe({
      next: (data: any) => {
        this.formFields = [
  <IndividualField>{
    generalProperties: {
      fieldApiName: 'email',
      fieldName: 'Email Address',
      fieldLabel: 'Email Address',
      fieldPlaceholder: 'admin@example.com',
      fieldType: 'IndividualField',
      inputType: 'email',
      isRequired: true,
      auth: {
        read: 'all',
        write: 'superadmin'
      },
      isArray: false,
      isGroup: false
    },
    data: null,
    dataType: 'string'
  },
  <IndividualField>{
    generalProperties: {
      fieldApiName: 'firstName',
      fieldName: 'First Name',
      fieldLabel: 'First Name',
      fieldPlaceholder: 'John',
      fieldType: 'IndividualField',
      isRequired: true,
      auth: {
        read: 'all',
        write: 'superadmin'
      },
      isArray: false,
      isGroup: false
    },
    data: null,
    dataType: 'string'
  },
  <IndividualField>{
    generalProperties: {
      fieldApiName: 'lastName',
      fieldName: 'Last Name',
      fieldLabel: 'Last Name',
      fieldPlaceholder: 'Doe',
      fieldType: 'IndividualField',
      isRequired: true,
      auth: {
        read: 'all',
        write: 'superadmin'
      },
      isArray: false,
      isGroup: false
    },
    data: null,
    dataType: 'string'
  },
  <IndividualField>{
    generalProperties: {
      fieldApiName: 'phoneNumber',
      fieldName: 'Phone Number',
      fieldLabel: 'Phone Number',
      fieldPlaceholder: '+234 800 000 0000',
      fieldType: 'IndividualField',
      inputType: 'tel',
      auth: {
        read: 'all',
        write: 'superadmin'
      },
      isArray: false,
      isGroup: false
    },
    data: null,
    dataType: 'string'
  }
];
      },
      error: (err: any) => {
        this.errorService.openandCloseError('Error occurred while preparing form fields');
        console.error(err);
      }
    });
  }
  onAccountTypeChange(type: 'SuperAdmin' | 'EventManager') {
    this.accountType = type;
    this.formMeta = {
      ...this.formMeta,
      formName: type === 'SuperAdmin' ? 'SuperAdmin Registration' : 'Global Event Manager Registration',
      formDescription: type === 'SuperAdmin'
        ? 'Create a new SuperAdmin user'
        : 'Create a new Global Event Manager (not tied to any organization)',
      submitText: type === 'SuperAdmin' ? 'Create SuperAdmin' : 'Create Event Manager'
    };
  }
  
onSubmit(values: any) {
    this.formValues = values;

    if (!values.email || !values.firstName || !values.lastName) {
      this.errorService.openandCloseError('Email, First Name, and Last Name are required');
      return;
    }

    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(values.email)) {
      this.errorService.openandCloseError('Please enter a valid email address');
      return;
    }

    const payload = {
      email: values.email.trim(),
      firstName: values.firstName.trim(),
      lastName: values.lastName.trim(),
      phoneNumber: values.phoneNumber?.trim() || null
    };

    const isEventManager = this.accountType === 'EventManager';
    const endpoint = isEventManager
      ? `${this.backendApi}/api/Admin/global-event-managers`
      : `${this.backendApi}/api/SuperAdmin/users`;

    this.http.post(endpoint, payload).subscribe({
      next: (response: any) => {
        console.log(`${this.accountType} created:`, response);

        const successMessage = isEventManager
          ? (response?.isNewAccount === false
              ? `Global Event Manager role granted to existing user ${values.email}`
              : `Global Event Manager created successfully! Temporary password sent to ${values.email}`)
          : `SuperAdmin user created successfully! Temporary password sent to ${values.email}`;

        this.snackBar.openFromComponent(SuccessMessageComponent, {
          data: { message: successMessage, action: 'Close' },
          duration: 5000,
          horizontalPosition: 'center',
          verticalPosition: 'top',
        });

        setTimeout(() => {
          this.router.navigate(['/app/Users']);
        }, 2000);
      },
      error: (error) => {
        console.error('Creation failed:', error);
        this.errorService.openandCloseError(
          error?.error?.message || `Failed to create ${isEventManager ? 'Global Event Manager' : 'SuperAdmin'} user`
        );
      }
    });
  }

  cancel() {
    this.router.navigate(['/app/Users']);
  }
}
